import type { StepRecord, StepFrame, ResultSummary } from "../types";

const isObject = (v: unknown): v is Record<string, unknown> =>
    typeof v === "object" && v !== null && !Array.isArray(v);

const finite = (v: unknown): number | null =>
    typeof v === "number" && Number.isFinite(v) ? v : null;

// Step records reach the client from two places we do not control: an SSE frame and the
// conversation GET. Both are coerced here, once, so nothing downstream needs a guard on every
// `data.x` read. A record with no usable id is dropped — it cannot be paired with a close,
// cannot be a React key, and would render as a blank row.
//
// Unknown `type` and `status` values are kept as they arrive rather than mapped to a default:
// the renderer already falls back to a neutral icon, and inventing a status would hide a
// backend change instead of showing it.
export const normalizeStep = (raw: unknown): StepRecord | null => {
    if (!isObject(raw) || typeof raw.id !== "string" || raw.id === "") {
        console.warn("[Chatbot] dropped a step record with no usable id", raw);
        return null;
    }
    return {
        id: raw.id,
        type: typeof raw.type === "string" ? raw.type : "unknown",
        status: typeof raw.status === "string" ? (raw.status as StepRecord["status"]) : "ok",
        label: typeof raw.label === "string" ? raw.label : raw.id,
        detail: typeof raw.detail === "string" && raw.detail !== "" ? raw.detail : null,
        started_ms: finite(raw.started_ms),
        ended_ms: finite(raw.ended_ms),
        data: isObject(raw.data) ? raw.data : {},
    };
};

export const normalizeSteps = (raw: unknown): StepRecord[] =>
    Array.isArray(raw) ? raw.map(normalizeStep).filter((s): s is StepRecord => s !== null) : [];

const patchLast = (steps: StepRecord[], id: string, patch: (s: StepRecord) => StepRecord): StepRecord[] => {
    const idx = steps.findLastIndex(s => s.id === id);
    if (idx === -1) {
        console.warn("[Chatbot] step frame for unknown id", id);
        return steps;
    }
    const next = [...steps];
    next[idx] = patch(steps[idx]);
    return next;
};

// The done frame's data is partial — only the keys the close site added — so it merges over the
// start data. Patches the last record with the id: a repeated id within one array can only
// happen when the server reuses a message_id, and the newest record is the live one.
export const applyStepFrame = (steps: StepRecord[], frame: StepFrame): StepRecord[] => {
    // The frame is wire data. A malformed one must not throw: this runs inside a setState
    // updater, so an exception here would take down the whole turn, not just one row.
    if (!isObject(frame) || !isObject((frame as unknown as { step?: unknown }).step)
        || typeof frame.step.id !== "string") {
        console.warn("[Chatbot] malformed step frame", frame);
        return steps;
    }
    switch (frame.type) {
        case "step.start":
            return [...steps, {
                id: frame.step.id,
                type: frame.step.type,
                status: "running",
                label: frame.step.label,
                detail: frame.step.detail ?? null,
                started_ms: finite(frame.step.ts),
                ended_ms: null,
                data: isObject(frame.step.data) ? frame.step.data : {},
            }];
        case "step.delta":
            return patchLast(steps, frame.step.id, s => ({
                ...s,
                data: { ...s.data, reasoning: `${typeof s.data.reasoning === "string" ? s.data.reasoning : ""}${typeof frame.step.delta === "string" ? frame.step.delta : ""}` },
            }));
        case "step.done":
            return patchLast(steps, frame.step.id, s => ({
                ...s, status: frame.step.status, ended_ms: finite(frame.step.ts), data: { ...s.data, ...(isObject(frame.step.data) ? frame.step.data : {}) },
            }));
        case "step.error":
            return patchLast(steps, frame.step.id, s => ({
                ...s, status: "error", ended_ms: finite(frame.step.ts), data: { ...s.data, error: frame.step.error },
            }));
    }
};

// No frame states a parent; nesting is by time. A render that starts while a compose is open
// (ended_ms null means still open) sits one level under it. Nothing else nests.
export const nestSteps = (steps: StepRecord[]): Array<{ step: StepRecord; depth: number }> => {
    const composes = steps.filter(s => s.type === "compose");
    return steps.map(step => {
        const start = step.started_ms ?? -Infinity;
        const inside = step.type === "render" && composes.some(c =>
            (c.started_ms ?? Infinity) <= start && start <= (c.ended_ms ?? Infinity));
        return { step, depth: inside ? 1 : 0 };
    });
};

export const formatDuration = (ms: number): string => (ms < 1000 ? `${Math.round(ms)}ms` : `${(ms / 1000).toFixed(1)}s`);

export const totalDuration = (steps: StepRecord[]): number | null => {
    const starts = steps.map(s => s.started_ms).filter((v): v is number => v != null);
    const ends = steps.map(s => s.ended_ms).filter((v): v is number => v != null);
    return starts.length && ends.length ? Math.max(...ends) - Math.min(...starts) : null;
};

// The peek window shows the turn's thinking as one running text. A turn can reason more than
// once — an agentic turn reasons before the fetches and again while it composes — so each trace
// appends under the one before instead of replacing it.
//
// Markdown marks are stripped: the window is a glance at the words as they arrive, not a
// rendered document, and a stray `##` in the box reads as noise.
//
// The underscore is NOT one of them. A trace names tools and metric keys — `get_gps_data`,
// `record_count` — and stripping it mangles every one of them. Underscore emphasis around a
// whole word is rare enough in a trace to leave alone, and it costs one character when it does
// appear. Heading marks go only at the start of a line, so a `#1` inside a sentence survives.
export const reasoningText = (steps: StepRecord[]): string =>
    steps
        .map(s => (typeof s.data.reasoning === "string" ? s.data.reasoning : ""))
        .filter(t => t.trim() !== "")
        .join("\n\n")
        .replace(/^#{1,6}\s+/gm, "")
        .replace(/[*`]/g, "")
        .replace(/\n{3,}/g, "\n\n")
        .trim();

// Where to cut a block of reasoning that is appended whole paragraphs at a time. `want` is how
// much the pacer asks for; this moves that cut to the nearest break so a paragraph lands whole
// rather than stopping mid-word.
//
// It looks forward first, within half again of `want`, because ending a step ON a break reads
// better than ending just before one. A block of solid text with no break in reach falls back
// to the raw count: the pacer's budget wins over tidiness.
export const blockCut = (text: string, want: number): number => {
    if (want >= text.length) return text.length;
    const limit = Math.ceil(want * 1.5);
    for (const sep of ["\n\n", "\n", " "]) {
        const ahead = text.indexOf(sep, want);
        if (ahead !== -1 && ahead < limit) return ahead + sep.length;
        const behind = text.lastIndexOf(sep, want);
        if (behind > 0) return behind + sep.length;
    }
    return want;
};

const plural = (n: number, word: string): string => `${n} ${word}${n === 1 ? "" : "s"}`;

// Parts, not one dot-joined string. The header lays them out itself, so a failure count can be
// styled as a warning instead of disappearing into a run of middle dots. No step count: the
// header states a duration, and a count beside it invites the reader to add the rows up and
// find they do not quite reach it.
export interface StepsSummary {
    duration: string | null;
    failed: number;
    partial: number;
    interrupted: boolean;
}

export const summarizeSteps = (steps: StepRecord[]): StepsSummary => {
    const total = totalDuration(steps);
    return {
        duration: total != null ? formatDuration(total) : null,
        failed: steps.filter(s => s.status === "error").length,
        partial: steps.filter(s => s.status === "partial").length,
        interrupted: steps.some(s => s.status === "running"),
    };
};

const metricsText = (m: unknown): string | undefined => {
    if (typeof m === "string") return m;
    if (Array.isArray(m) && m.every(x => typeof x === "string")) return m.join(", ");
    return undefined;
};

// One readable line for a coach: what was pulled, over which window, how much came back.
// Commas, not middle dots, and no `dimension` — "aggregate" and "player" are internal words.
// Sentence case, because this reads as prose under the step label rather than as a data chip.
//
// Every key is optional. The agentic path sends all five; summary v2 sends `record_count`
// alone (brief §5.4), so this has to read whatever it finds rather than assume a shape.
export const describeResult = (r: ResultSummary | undefined): string => {
    if (!r) return "";
    const subject = [r.subject, metricsText(r.metrics)].filter(Boolean).join(" ");
    const count = r.record_count != null ? plural(r.record_count, "record") : undefined;
    const text = [subject || undefined, r.date_range, count].filter(Boolean).join(", ");
    return text ? text.charAt(0).toUpperCase() + text.slice(1) : "";
};

if (import.meta.env?.DEV) {
    const mid = "7-turn0-assistant";
    const rec = (p: Partial<StepRecord> & { id: string; type: string }): StepRecord =>
        ({ status: "ok", label: p.id, detail: null, started_ms: 0, ended_ms: 1, data: {}, ...p });

    // done replaces instead of merging → tool lost
    const start: StepFrame = { type: "step.start", message_id: mid, step: { id: "fetch:get_gps_data", type: "fetch", label: "Fetching GPS data", detail: null, ts: 1000, data: { tool: "get_gps_data" } } };
    const done: StepFrame = { type: "step.done", message_id: mid, step: { id: "fetch:get_gps_data", status: "ok", ts: 1640, data: { result_summary: { record_count: 42 } } } };
    const s = applyStepFrame(applyStepFrame([], start), done);
    console.assert(s[0].status === "ok" && s[0].data.tool === "get_gps_data" && (s[0].data.result_summary as ResultSummary).record_count === 42 && s[0].ended_ms === 1640, "done merges over start");

    // delta replaces or prepends
    // Fixture labels are deliberately nonsense. The backend reworded the whole set twice in
    // two days (brief §5.6), and nothing here may depend on the wording.
    const agent: StepFrame = { type: "step.start", message_id: mid, step: { id: "plan:agent", type: "plan", label: "L", detail: null, ts: 0 } };
    let r = applyStepFrame([], agent);
    r = applyStepFrame(r, { type: "step.delta", message_id: mid, step: { id: "plan:agent", ts: 1, delta: "ab" } });
    r = applyStepFrame(r, { type: "step.delta", message_id: mid, step: { id: "plan:agent", ts: 2, delta: "cd" } });
    console.assert(r[0].data.reasoning === "abcd", "delta appends");

    // error case copy-pasted from done
    const e = applyStepFrame(applyStepFrame([], agent), { type: "step.error", message_id: mid, step: { id: "plan:agent", ts: 9, error: "boom" } });
    console.assert(e[0].status === "error" && e[0].data.error === "boom" && e[0].ended_ms === 9, "error sets status, error, ended_ms");

    // unknown id creates or copies
    const before = applyStepFrame([], agent);
    console.assert(applyStepFrame(before, { type: "step.done", message_id: mid, step: { id: "nope", status: "ok", ts: 5 } }) === before, "unknown id returns the same array");

    // repeated id patches the first record instead of the last
    let two = applyStepFrame(applyStepFrame([], agent), agent);
    two = applyStepFrame(two, { type: "step.done", message_id: mid, step: { id: "plan:agent", status: "ok", ts: 3 } });
    console.assert(two[0].status === "running" && two[1].status === "ok", "patch hits the last record with that id");

    // open compose treated as closed → live renders never nest
    const openCompose = rec({ id: "compose", type: "compose", status: "running", started_ms: 100, ended_ms: null });
    const inner = rec({ id: "render:RenderChart", type: "render", started_ms: 200, ended_ms: 300 });
    console.assert(nestSteps([openCompose, inner])[1].depth === 1, "render inside an open compose nests");
    const closedCompose = rec({ id: "compose", type: "compose", started_ms: 100, ended_ms: 900 });
    const after = rec({ id: "render:RenderChart:2", type: "render", started_ms: 950, ended_ms: 990 });
    console.assert(nestSteps([closedCompose, after])[1].depth === 0, "render after compose closed stays flat");

    // header loses the failure count, or counts a failure twice
    const hdr = summarizeSteps([
        rec({ id: "plan", type: "plan", started_ms: 0, ended_ms: 100 }),
        rec({ id: "fetch:a", type: "fetch", started_ms: 100, ended_ms: 200 }),
        rec({ id: "fetch:a:2", type: "fetch", status: "error", started_ms: 200, ended_ms: 300 }),
    ]);
    console.assert(hdr.duration === "300ms" && hdr.failed === 1 && hdr.partial === 0 && !hdr.interrupted, `summarizeSteps: ${JSON.stringify(hdr)}`);
    const intr = summarizeSteps([rec({ id: "plan", type: "plan", started_ms: 0, ended_ms: 100 }), rec({ id: "compose", type: "compose", status: "running", started_ms: 100, ended_ms: null })]);
    console.assert(intr.interrupted && intr.duration === "100ms", `summarizeSteps interrupted: ${JSON.stringify(intr)}`);

    // a malformed frame throws instead of being ignored — it runs inside a setState updater
    const good = applyStepFrame([], agent);
    console.assert(applyStepFrame(good, { type: "step.done" } as unknown as StepFrame) === good, "a frame with no step is ignored");
    console.assert(applyStepFrame(good, { type: "step.done", message_id: mid, step: { ts: 1 } } as unknown as StepFrame) === good, "a step with no id is ignored");
    console.assert(applyStepFrame([], { type: "step.start", message_id: mid, step: { id: "x", type: "fetch", label: "L", detail: null, ts: 1, data: null } } as unknown as StepFrame)[0].data !== null, "a null data on start becomes an object");

    // a stored record with missing or wrong-typed fields reaches the renderer unguarded
    console.assert(normalizeStep({ id: "plan" })?.data !== undefined, "a record with no data gets an object");
    console.assert(normalizeStep({ id: "plan", data: "nope" })?.data !== "nope" as unknown, "a non-object data is replaced");
    console.assert(normalizeStep({ id: "plan" })?.label === "plan", "a missing label falls back to the id");
    console.assert(normalizeStep({ id: "plan", started_ms: "12" })?.started_ms === null, "a string timestamp becomes null");
    console.assert(normalizeStep({ id: "plan", detail: "" })?.detail === null, "an empty detail becomes null");
    console.assert(normalizeStep({ type: "plan" }) === null, "a record with no id is dropped");
    console.assert(normalizeStep(null) === null, "null is dropped");
    console.assert(normalizeSteps([{ id: "a" }, null, { nope: 1 }, { id: "b" }]).length === 2, "normalizeSteps keeps only usable records");
    console.assert(normalizeSteps(undefined).length === 0, "a missing steps key yields no records");
    // legacy dev-era ids must survive untouched
    console.assert(normalizeStep({ id: "s1", type: "plan", status: "ok", label: "L" })?.id === "s1", "an s1 id is kept");

    // The peek shows one trace only, or raw markdown, or the traces in the wrong order.
    console.assert(reasoningText([]) === "", "a turn that did not reason gives no peek text");
    const reasoned = [
        rec({ id: "plan", type: "plan", data: { reasoning: "**First** thought" } }),
        rec({ id: "fetch:a", type: "fetch" }),
        rec({ id: "compose", type: "compose", data: { reasoning: "Second thought" } }),
    ];
    console.assert(reasoningText(reasoned) === "First thought\n\nSecond thought", `reasoningText: ${JSON.stringify(reasoningText(reasoned))}`);
    // A trace names its tools and metric keys. Stripping the underscore mangled every one.
    const keys = [rec({ id: "plan", type: "plan", data: { reasoning: "## Plan\nCall `get_gps_data` for total_distance, then check record_count." } })];
    console.assert(reasoningText(keys) === "Plan\nCall get_gps_data for total_distance, then check record_count.", `reasoningText keys: ${JSON.stringify(reasoningText(keys))}`);

    // A block cut lands mid-word, or runs away looking for a break that is not there.
    const paras = "aaaa\n\nbbbb\n\ncccc";
    console.assert(blockCut(paras, 5) === 6, `blockCut takes the paragraph break ahead: ${blockCut(paras, 5)}`);
    console.assert(blockCut(paras, 100) === paras.length, "a want past the end returns the length");
    const solid = "x".repeat(500);
    console.assert(blockCut(solid, 100) === 100, "solid text with no break falls back to the raw count");
    console.assert(blockCut(`${"x".repeat(100)} tail`, 40) === 40, "a break too far ahead does not drag the cut out");
    const words = "one two three four five";
    console.assert(words[blockCut(words, 9) - 1] === " ", `a cut inside a word moves to a space: ${JSON.stringify(words.slice(0, blockCut(words, 9)))}`);

    console.assert(formatDuration(640) === "640ms" && formatDuration(4210) === "4.2s", "formatDuration");
    const desc = describeResult({ subject: "team", metrics: ["distance"], date_range: "2026-09-07 to 2026-09-13", record_count: 1, dimension: "aggregate" });
    console.assert(desc === "Team distance, 2026-09-07 to 2026-09-13, 1 record", `describeResult: ${desc}`);
    console.assert(!describeResult({ subject: "team", dimension: "player" }).includes("player"), "describeResult drops the dimension");
    console.assert(describeResult({ record_count: 4 }) === "4 records", "describeResult plural");
    console.assert(describeResult({ subject: "A", metrics: { k: 1 } }) === "A", "describeResult ignores non-string metrics");
    // Summary v2 sends record_count alone (brief §5.4) — reading all five keys would blank it.
    console.assert(describeResult({ record_count: 10 }) === "10 records", "a count-only summary still reads");

    // A crash in a nested step now closes both, innermost first, same message (brief §11.4).
    let nested = applyStepFrame([], { type: "step.start", message_id: mid, step: { id: "compose", type: "compose", label: "L", detail: null, ts: 0 } });
    nested = applyStepFrame(nested, { type: "step.start", message_id: mid, step: { id: "render:RenderChart", type: "render", label: "L", detail: null, ts: 1 } });
    nested = applyStepFrame(nested, { type: "step.error", message_id: mid, step: { id: "render:RenderChart", ts: 2, error: "boom" } });
    nested = applyStepFrame(nested, { type: "step.error", message_id: mid, step: { id: "compose", ts: 3, error: "boom" } });
    console.assert(nested.every(s => s.status === "error"), "both steps of a nested crash close");
    console.assert(nested.every(s => s.ended_ms != null), "neither is left running");
}
